<template>
  <div>
    <v-data-table
      disable-sort
      hide-default-footer
      mobile-breakpoint="20"
      :headers="headers"
      :loading="isLoading"
      :items="hausefulTakeRateInvoices"
      :items-per-page="itemsPerPage"
      :no-data-text="$t('appointment.table.noData')"
      :loading-text="$t('appointment.table.loading')"
    >
      <template v-slot:[`item.created_at`]="{ item }">
        {{ formatDate(item.created_at) }}
      </template>
      <template v-slot:[`item.created_by`]="{ item }">
        <!-- Nota emitida direto no painel do Asaas e importada pelo webhook não tem autor aqui -->
        <span class="d-block text-truncate">
          {{ item.created_by ? item.created_by.name : $t('hausefulInvoices.emittedOutsideThePlatform') }}
        </span>
      </template>
      <template v-slot:[`item.real_estate`]="{ item }">
        <div v-html="customer(item)" />
      </template>
      <template v-slot:[`item.branch`]="{ item }">
        <span class="d-block text-truncate">
          {{ item.branch ? item.branch.name : '---' }}
        </span>
      </template>
      <template v-slot:[`item.asaas_invoice`]="{ item }">
        <v-chip
          v-if="item.asaas_invoice"
          :color="asaasStatusColor(item.asaas_invoice.status)"
          class="caption"
          small
          outlined
        >
          {{ $t(`hausefulInvoices.asaasStatus.${item.asaas_invoice.status}`) }}
        </v-chip>
        <span v-else class="grey--text caption">---</span>
        <InformationTooltip
          v-if="item.asaas_invoice && item.asaas_invoice.status_description"
          :message="item.asaas_invoice.status_description"
        />
      </template>
      <template v-slot:[`item.payment_link`]="{ item }">
        <!-- Nota importada do painel do Asaas que ninguém casou com os pagamentos ainda:
             enquanto isso as vistorias seguem na aba Pendentes -->
        <v-chip
          v-if="isMissingPaymentLink(item)"
          @click="callLinkPaymentsModal(item)"
          color="warning"
          class="caption"
          small
          outlined
        >
          {{ $t('hausefulInvoices.withoutPaymentLink') }}
        </v-chip>
        <span v-else class="grey--text caption">---</span>
      </template>
      <template v-slot:[`item.file_response`]="{ item }">
        <v-btn v-if="item.file_response" icon color="grey" elevation="0" @click="openInvoicePdf(item)">
          <IconTooltip icon="mdi-download" :description="item.file_response.name" color="grey" />
        </v-btn>
        <span v-else class="grey--text caption">---</span>
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <v-menu offset-y bottom left>
          <template v-slot:activator="{ on }">
            <v-btn icon v-on="on">
              <v-icon small>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item
              v-if="invoicePdfUrl(item)"
              @click="openInvoicePdf(item)"
            >
              <v-icon class="mr-3 grey--text text--lighten-1" small left>mdi-file-pdf-box</v-icon>
              <v-list-item-title class="d-flex">
                {{ $t('hausefulInvoices.actions.openInvoicePdf') }}
              </v-list-item-title>
            </v-list-item>
            <v-list-item
              v-if="isMissingPaymentLink(item)"
              @click="callLinkPaymentsModal(item)"
            >
              <v-icon class="mr-3 grey--text text--lighten-1" small left>mdi-link-variant</v-icon>
              <v-list-item-title class="d-flex">
                {{ $t('hausefulInvoices.actions.linkPayments') }}
              </v-list-item-title>
            </v-list-item>
            <v-list-item @click="callShowModal(item)">
              <v-icon class="mr-3 grey--text text--lighten-1" small left>mdi-eye-outline</v-icon>
              <v-list-item-title class="d-flex">
                {{ $t('hausefulInvoices.actions.show') }}
              </v-list-item-title>
            </v-list-item>
            <v-list-item @click="callEditModal(item)">
              <v-icon class="mr-3 grey--text text--lighten-1" small left>mdi-pencil-outline</v-icon>
              <v-list-item-title class="d-flex">
                {{ $t('hausefulInvoices.actions.edit') }}
              </v-list-item-title>
            </v-list-item>
            <v-divider />
            <v-list-item @click="callExcludeModal(item)">
              <v-icon class="mr-3 red--text text--lighten-2" small left>mdi-delete-outline</v-icon>
              <v-list-item-title class="d-flex red--text">
                {{ $t('hausefulInvoices.actions.exclude') }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </v-data-table>

    <v-divider />

    <h-pagination
      v-if="hausefulTakeRateInvoices.length >= 1"
      :fetch="fetchHausefulTakeRateInvoices"
      noBorders
    />
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { hausefulTakeRateInvoices } from './hausefulTakeRateInvoicesTableHeader'
import { catalogDateWithoutTime } from '@/util/catalog'
import InformationTooltip from '@/components/app/InformationTooltip'
import IconTooltip from '@/components/IconTooltip'
import { customer } from '@/util/customer'

export default {
  name: 'HausefulTakeRateInvoicesList',

  components: {
    IconTooltip,
    InformationTooltip,
  },

  data() {
    return {
      customer,
      headers: hausefulTakeRateInvoices,
      formatDate: catalogDateWithoutTime,
    }
  },

  computed: {
    ...mapGetters(['isLoading', 'itemsPerPage', 'hausefulTakeRateInvoices']),
  },

  methods: {
    ...mapMutations(['setHausefulInvoiceInfo']),
    ...mapActions(['openModal', 'fetchHausefulTakeRateInvoices', 'fetchPaymentLinkSuggestions']),

    asaasStatusColor(status) {
      if (status === 'AUTHORIZED') return 'success'
      if (status === 'ERROR' || status === 'CANCELLATION_DENIED') return 'error'
      if (status === 'CANCELED') return 'grey'
      return 'warning'
    },

    // Preferimos o arquivo do nosso S3 (o mesmo que a imobiliária baixa) e caímos no link do Asaas
    // enquanto o PDF da NFS-e ainda não foi anexado pelo webhook.
    invoicePdfUrl(item) {
      return item.file_response?.url ?? item.asaas_invoice?.pdf_url ?? null
    },

    openInvoicePdf(item) {
      const url = this.invoicePdfUrl(item)
      if (url) window.open(url, '_blank', 'noopener')
    },

    // Só notas vindas do Asaas ficam sem pagamentos: as emitidas pela plataforma nascem vinculadas
    isMissingPaymentLink(item) {
      return !!item.asaas_invoice && !item.payment_list?.length
    },

    callLinkPaymentsModal(item) {
      this.setHausefulInvoiceInfo(item)
      this.fetchPaymentLinkSuggestions(item.id)
      this.$store.dispatch('openModal', 'HAUSEFUL_INVOICE_LINK_PAYMENTS_MODAL')
    },

    callShowModal(item) {
      this.setHausefulInvoiceInfo(item)
      this.$store.dispatch('openModal', 'HAUSEFUL_INVOICE_SHOW_MODAL')
    },

    callEditModal(item) {
      this.setHausefulInvoiceInfo(item)
      this.$store.dispatch('openModal', 'HAUSEFUL_INVOICE_UPLOAD_MODAL')
    },

    callExcludeModal(item) {
      this.setHausefulInvoiceInfo(item)
      this.$store.dispatch('openModal', 'HAUSEFUL_INVOICE_DELETE_MODAL')
    },
  },
}
</script>
